import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  Button,
  makeStyles,
  useTheme
} from '@material-ui/core'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CustomFilter from '../../../../components/shared/customFilter/customFilter'
import DateFilter from '../../../../components/shared/dateFilter/dateFilter'
import { DateFilters } from '../../../../model'
import { RegulatoryReportsTypes } from '../../regulatoryReports.Model'
import { styles } from './reportCard.styles'

export interface FilterDates {
  startDate: Date
  endDate: Date
}

export interface CardProps {
  cardName: string
  dates: FilterDates
  commodity: number
}

// "cardName" must be on translation files
// regulatoryReports:components.reportCard.infoConsumosTitle.{cardName},
// regulatoryReports:components.reportCard.infoConsumosDescription.{cardName},
// regulatoryReports:components.reportCard.generateReportAction.{cardName}
export interface ReportCardProps {
  cardName: string
  onClick: (props: CardProps) => void
}

export const ReportCard: React.FC<ReportCardProps> = ({ cardName, onClick }) => {
  const [t] = useTranslation(['regulatoryReports'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [isFetching, setIsFetching] = useState<boolean>(false)

  const [customFilter, setCustomFilter] = useState<FilterDates | undefined>(undefined)
  const [filter, setFilter] = useState<FilterDates & { activeDateFilter: DateFilters | undefined }>(
    {
      startDate: moment().subtract(1, 'months').startOf('month').toDate(),
      endDate: moment().subtract(1, 'months').endOf('month').toDate(),
      activeDateFilter: DateFilters.LASTMONTH
    }
  )

  const onSelectDateHandler = (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters
  ): void => {
    setFilter({ startDate, endDate, activeDateFilter })
    setCustomFilter(undefined)
  }

  const onApplyCustomFilter = async (filterToApply: any) => {
    setFilter({
      startDate: filterToApply.startDate,
      endDate: filterToApply.endDate,
      activeDateFilter: DateFilters.CUSTOM
    })
    setCustomFilter({
      startDate: filterToApply.startDate,
      endDate: filterToApply.endDate
    })
  }

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    return true
  }

  const handleClick = () => {
    const dates: FilterDates = {
      startDate: customFilter?.startDate ?? filter.startDate,
      endDate: customFilter?.endDate ?? filter.endDate
    }
    onClick({
      cardName,
      dates,
      commodity: 1
    })
  }

  return (
    <Card style={{ height: '100%' }}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardContentPadding}>
          <Typography variant="subtitle1">
            {t(`regulatoryReports:components.reportCard.infoConsumosTitle.${cardName}`)}
          </Typography>
          <Typography variant="body2" className={classes.description}>
            {t(`regulatoryReports:components.reportCard.infoConsumosDescription.${cardName}`)}
          </Typography>
        </div>
        <div>
          <Divider />
          <div className={classes.cardContentPadding}>
            <Grid container>
              <div className={classes.dateFilterContainer}>
                <DateFilter
                  inactive={!!customFilter}
                  enabled={!isFetching}
                  initialFilter={
                    filter?.activeDateFilter === null || filter?.activeDateFilter === undefined
                      ? DateFilters.LASTMONTH
                      : filter.activeDateFilter
                  }
                  onSelectDate={(startDate, endDate, activeDateFilter) =>
                    onSelectDateHandler(startDate, endDate, activeDateFilter)
                  }
                  filters={[
                    {
                      filter: DateFilters.LASTMONTH,
                      label: t('translation:components.dateFilter.lastClosedMonth')
                    },
                    {
                      filter: DateFilters.LAST_CLOSED_QUARTER,
                      label: t('translation:components.dateFilter.lastClosedQuarter')
                    },
                    {
                      filter: DateFilters.LAST_CLOSED_YEAR,
                      label: t('translation:components.dateFilter.lastClosedYear')
                    },
                    {
                      filter: DateFilters.CUSTOM,
                      subtitle:
                        'En función del número de días del periodo seleccionado se rellenarán las pestañas correspondientes (mensual <31 días, trimestral <93 días, anual > 93 días)'
                      // label: t('translation:components.dateFilter.lastClosedYear')
                    }
                  ]}
                ></DateFilter>
              </div>
            </Grid>
            <Grid container justify="flex-end" className={classes.buttonContainer}>
              <Button variant="text" color="primary" disabled={false} onClick={handleClick}>
                {t(`regulatoryReports:components.reportCard.generateReportAction.${cardName}`)}
              </Button>
            </Grid>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
